import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    useProjects,
    useCreateProject,
    useUpdateProject,
    useDeleteProject,
} from "../../api/projects";
import { ProjectFunding, ProjectTranslationsPayload } from "../../types/projects";

const SUPPORTED_LANGUAGES = ["ru", "en", "uk", "ka"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

interface TranslationFormState {
    name: string;
    short_description: string;
    brief_description: string;
    description: string;
}

interface ProjectFormState {
    translations: Record<string, TranslationFormState>;
    target_amount: string;
    min_subscription_amount: string;
    max_coefficient: string;
    dividend_pool_percent: string;
    sort_order: string;
    is_subscription_active: boolean;
    logo: File | null;
}

const createEmptyTranslations = (): Record<string, TranslationFormState> =>
    SUPPORTED_LANGUAGES.reduce(
        (acc, locale) => ({
            ...acc,
            [locale]: {
                name: "",
                short_description: "",
                brief_description: "",
                description: "",
            },
        }),
        {} as Record<string, TranslationFormState>
    );

const createDefaultFormState = (): ProjectFormState => ({
    translations: createEmptyTranslations(),
    target_amount: "",
    min_subscription_amount: "10",
    max_coefficient: "10",
    dividend_pool_percent: "50",
    sort_order: "0",
    is_subscription_active: true,
    logo: null,
});

const ProjectsList: React.FC = () => {
    const languages = useMemo<SupportedLanguage[]>(() => [...SUPPORTED_LANGUAGES], []);
    const defaultLocale = languages[0];

    const [page, setPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState<ProjectFormState>(() => createDefaultFormState());
    const [activeLocale, setActiveLocale] = useState<SupportedLanguage>(defaultLocale);
    const [editingProject, setEditingProject] = useState<ProjectFunding | null>(
        null
    );
    const [formError, setFormError] = useState<string | null>(null);

    const { data, isLoading, isError, error } = useProjects({ page });
    const createProject = useCreateProject();
    const updateProject = useUpdateProject();
    const deleteProject = useDeleteProject();

    const projects = data?.data ?? [];
    const meta = data?.meta;

    const openCreateModal = () => {
        setEditingProject(null);
        setFormState(createDefaultFormState());
        setActiveLocale(defaultLocale);
        setFormError(null);
        setIsModalOpen(true);
    };

    const openEditModal = (project: ProjectFunding) => {
        setEditingProject(project);

        const translationsState = createEmptyTranslations();

        project.translations?.forEach((translation) => {
            if (!translation?.locale) {
                return;
            }

            const locale = translation.locale as string;

            translationsState[locale] = {
                name: translation.name ?? "",
                short_description: translation.short_description ?? "",
                brief_description: translation.brief_description ?? "",
                description: translation.description ?? "",
            };
        });

        if (!translationsState[defaultLocale]) {
            translationsState[defaultLocale] = {
                name: "",
                short_description: "",
                brief_description: "",
                description: "",
            };
        }

        translationsState[defaultLocale] = {
            name: translationsState[defaultLocale].name || project.name || "",
            short_description:
                translationsState[defaultLocale].short_description ||
                project.short_description ||
                "",
            brief_description:
                translationsState[defaultLocale].brief_description ||
                project.brief_description ||
                "",
            description:
                translationsState[defaultLocale].description || project.description || "",
        };

        setFormState({
            translations: translationsState,
            target_amount: project.target_amount ?? "",
            min_subscription_amount: project.min_subscription_amount ?? "",
            max_coefficient: String(project.max_coefficient ?? 10),
            dividend_pool_percent: String(
                project.dividend_pool_percent ?? 50
            ),
            sort_order: String(project.sort_order ?? 0),
            is_subscription_active: !!project.is_subscription_active,
            logo: null,
        });
        setActiveLocale(defaultLocale);
        setFormError(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingProject(null);
        setFormError(null);
        setFormState(createDefaultFormState());
        setActiveLocale(defaultLocale);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const baseTranslation = formState.translations[defaultLocale] ?? {
            name: "",
            short_description: "",
            brief_description: "",
            description: "",
        };

        if (!baseTranslation.name.trim()) {
            setFormError(`Название обязательно для языка ${defaultLocale.toUpperCase()}.`);
            setActiveLocale(defaultLocale);
            return;
        }
        if (!formState.target_amount) {
            setFormError("Требуется указать ожидаемую сумму сбора.");
            return;
        }
        const dividendPercent = Number(formState.dividend_pool_percent);
        if (
            Number.isNaN(dividendPercent) ||
            dividendPercent < 0 ||
            dividendPercent > 100
        ) {
            setFormError("Укажите корректный процент распределения дивидендов (0-100).");
            return;
        }

        const maxCoefficientValue = Number(formState.max_coefficient);
        if (
            Number.isNaN(maxCoefficientValue) ||
            maxCoefficientValue < 1 ||
            maxCoefficientValue > 100
        ) {
            setFormError("Укажите корректный максимальный коэффициент (1-100).");
            return;
        }

        const translationsPayload: ProjectTranslationsPayload = {};

        for (const locale of languages) {
            const current = formState.translations[locale] ?? {
                name: "",
                short_description: "",
                brief_description: "",
                description: "",
            };

            const name = current.name.trim();
            const shortDescription = current.short_description.trim();
            const briefDescription = current.brief_description.trim();
            const description = current.description.trim();

            if (locale === defaultLocale) {
                translationsPayload[locale] = {
                    name,
                    short_description: shortDescription.length > 0 ? shortDescription : null,
                    brief_description: briefDescription.length > 0 ? briefDescription : null,
                    description: description.length > 0 ? description : null,
                };
                continue;
            }

            const hasContent =
                name.length > 0 || shortDescription.length > 0 || briefDescription.length > 0 || description.length > 0;

            if (!hasContent) {
                continue;
            }

            if (name.length === 0) {
                setFormError(
                    `Введите название для языка ${locale.toUpperCase()} или удалите заполненные поля.`
                );
                setActiveLocale(locale);
                return;
            }

            translationsPayload[locale] = {
                name,
                short_description: shortDescription.length > 0 ? shortDescription : null,
                brief_description: briefDescription.length > 0 ? briefDescription : null,
                description: description.length > 0 ? description : null,
            };
        }

        const basePayload = {
            translations: translationsPayload,
            target_amount: formState.target_amount,
            min_subscription_amount: formState.min_subscription_amount,
            max_coefficient: maxCoefficientValue,
            dividend_pool_percent: dividendPercent,
            sort_order:
                formState.sort_order.trim() === ""
                    ? undefined
                    : Number(formState.sort_order),
            is_subscription_active: formState.is_subscription_active,
            logo: formState.logo,
        };

        try {
            if (editingProject) {
                await updateProject.mutateAsync({
                    id: editingProject.id,
                    ...basePayload,
                });
            } else {
                await createProject.mutateAsync(basePayload);
            }
            closeModal();
        } catch (err: any) {
            setFormError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Ошибка сохранения проекта."
            );
        }
    };

    const handleDelete = async (project: ProjectFunding) => {
        if (
            window.confirm(
                `Удалить проект "${project.name}"? Это действие необратимо.`
            )
        ) {
            try {
                await deleteProject.mutateAsync(project.id);
            } catch (err: any) {
                window.alert(
                    err?.response?.data?.message ||
                        err?.message ||
                        "Не удалось удалить проект."
                );
            }
        }
    };

    const renderStatusBadge = (project: ProjectFunding) => {
        if (!project.is_subscription_active) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                    Остановлена
                </span>
            );
        }

        if (project.launch_date) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                    Запущен
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                Активна
            </span>
        );
    };

    const modalTitle = useMemo(
        () => (editingProject ? "Редактирование проекта" : "Новый проект"),
        [editingProject]
    );

    const updateTranslationField = (
        locale: SupportedLanguage,
        field: keyof TranslationFormState,
        value: string
    ) => {
        setFormState((prev) => ({
            ...prev,
            translations: {
                ...prev.translations,
                [locale]: {
                    ...(prev.translations[locale] ?? {
                        name: "",
                        short_description: "",
                        brief_description: "",
                        description: "",
                    }),
                    [field]: value,
                },
            },
        }));
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Проекты и подписки
                </h1>
                <button
                    onClick={openCreateModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                >
                    Добавить проект
                </button>
            </div>

            {isLoading && (
                <div className="text-gray-500 dark:text-gray-300">
                    Загрузка проектов...
                </div>
            )}

            {isError && (
                <div className="text-red-500">
                    {(error as any)?.message || "Не удалось загрузить список."}
                </div>
            )}

            {!isLoading && !isError && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Название
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Статус
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Сбор / Цель
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Прогресс
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Подписки
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {projects.map((project) => (
                                <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center space-x-3">
                                            {project.logo_url ? (
                                                <img
                                                    src={project.logo_url}
                                                    alt={project.name}
                                                    className="w-10 h-10 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500">
                                                    {project.name.slice(0, 1)}
                                                </div>
                                            )}
                                            <div>
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {project.name}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Минимальная сумма: {project.min_subscription_amount} USD • Макс. коэффициент:{" "}
                                                    {project.max_coefficient}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderStatusBadge(project)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                            {project.collected_amount}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            из {project.target_amount}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="w-40">
                                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded">
                                                <div
                                                    className="h-2 bg-blue-500 rounded"
                                                    style={{
                                                        width: `${Math.min(
                                                            100,
                                                            project.progress_percent
                                                        ).toFixed(0)}%`,
                                                    }}
                                                ></div>
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {project.progress_percent.toFixed(1)}%
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                                        <div>
                                            Активных:{" "}
                                            {project.active_subscriptions_count ?? 0}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Всего: {project.total_subscriptions_count ?? 0}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-medium">
                                        <div className="inline-flex items-center gap-2">
                                            <Link
                                                to={`/dashboard/projects/${project.id}`}
                                                className="inline-flex items-center justify-center p-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                                title="Детали"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 5a9 9 0 100 18 9 9 0 000-18zm0 0V1m0 0l3 3m-3-3l-3 3"
                                                    />
                                                </svg>
                                            </Link>
                                            <button
                                                onClick={() => openEditModal(project)}
                                                className="inline-flex items-center justify-center p-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                                title="Изменить"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l10-10a1 1 0 000-1.414l-3.586-3.586a1 1 0 00-1.414 0l-10 10A1 1 0 004 15.414V20z"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(project)}
                                                className="inline-flex items-center justify-center p-2 bg-red-500 text-white rounded hover:bg-red-600 transition focus:outline-none focus:ring-2 focus:ring-red-400"
                                                title="Удалить"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-5 4V4m6 0v3m5 0H4"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {projects.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={6}
                                        className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        Проекты пока не созданы.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {meta && (
                <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-300">
                    <div>
                        Страница {meta.current_page} из {meta.last_page}. Всего проектов:{" "}
                        {meta.total}.
                    </div>
                    <div className="space-x-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            className={`px-3 py-1 rounded border ${
                                page <= 1
                                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                            Назад
                        </button>
                        <button
                            disabled={page >= meta.last_page}
                            onClick={() =>
                                setPage((prev) => Math.min(meta.last_page, prev + 1))
                            }
                            className={`px-3 py-1 rounded border ${
                                page >= meta.last_page
                                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                            Далее
                        </button>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 p-4 sm:p-6 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl p-6 relative max-h-[calc(100vh-2rem)] overflow-y-auto">
                        <button
                            onClick={closeModal}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-semibold"
                            aria-label="Закрыть модальное окно"
                        >
                            ×
                        </button>
                        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                            {modalTitle}
                        </h2>
                        {formError && (
                            <div className="mb-4 text-sm text-red-500">{formError}</div>
                        )}
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                                    <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
                                        {languages.map((lng) => (
                                            <button
                                                key={lng}
                                                type="button"
                                                onClick={() => setActiveLocale(lng)}
                                                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${
                                                    activeLocale === lng
                                                        ? "border-blue-600 text-blue-600"
                                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                                }`}
                                            >
                                                {lng.toUpperCase()}
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Название ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={formState.translations[activeLocale]?.name ?? ""}
                                            onChange={(e) =>
                                                updateTranslationField(activeLocale, "name", e.target.value)
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required={activeLocale === defaultLocale}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Краткое описание ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                formState.translations[activeLocale]?.short_description ?? ""
                                            }
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "short_description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Краткое описание после ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                formState.translations[activeLocale]?.brief_description ?? ""
                                            }
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "brief_description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Полное описание ({activeLocale.toUpperCase()})
                                        </label>
                                        <textarea
                                            value={formState.translations[activeLocale]?.description ?? ""}
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Статус подписки
                                </label>
                                <select
                                    value={formState.is_subscription_active ? "1" : "0"}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            is_subscription_active: e.target.value === "1",
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="1">Активна</option>
                                    <option value="0">Остановлена</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    План сбора (USD)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formState.target_amount}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            target_amount: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Минимальная подписка (USD)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formState.min_subscription_amount}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            min_subscription_amount: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Максимальный коэффициент
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    step="1"
                                    value={formState.max_coefficient}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            max_coefficient: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Доля дивидендов (%)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={formState.dividend_pool_percent}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            dividend_pool_percent: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Порядок сортировки
                                </label>
                                <input
                                    type="number"
                                    value={formState.sort_order}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            sort_order: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Логотип проекта
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            logo: e.target.files?.[0] ?? null,
                                        }))
                                    }
                                    className="w-full"
                                />
                                {editingProject?.logo_url && !formState.logo && (
                                    <div className="text-xs text-gray-500 mt-1">
                                        Текущий логотип будет сохранен, если не загрузить новый.
                                    </div>
                                )}
                            </div>
                            <div className="md:col-span-2 flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                >
                                    Сохранить
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsList;

